import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { getContact, updateContact, deleteContact } from '../firebase/contacts'
import { subscribeToContactActivities, logEmail, logCall, logMeeting, logWebsiteVisit, logLinkedInMessage } from '../firebase/activities'
import { format } from 'date-fns'
import ActivityForm from './ActivityForm'
import ContactForm from './ContactForm'
import LinkedInWorkflow from './LinkedInWorkflow'
import './ContactDetail.css'

const ContactDetail = ({ user }) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [contact, setContact] = useState(null)
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [showActivityForm, setShowActivityForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [activityType, setActivityType] = useState('email')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    if (!user || !id) return

    // Load contact
    const loadContact = async () => {
      try {
        const contactData = await getContact(id)
        if (!contactData) {
          navigate('/contacts')
          return
        }
        // Check if user owns this contact
        if (contactData.owner !== user.uid) {
          navigate('/contacts')
          return
        }
        setContact(contactData)
        setLoading(false)
      } catch (error) {
        console.error('Error loading contact:', error)
        setLoading(false)
      }
    }

    loadContact()

    // Subscribe to real-time activities
    const unsubscribe = subscribeToContactActivities(id, user.uid, (activitiesData) => {
      setActivities(activitiesData)
    })

    return () => unsubscribe()
  }, [id, user, navigate])

  const handleUpdateContact = async (contactData) => {
    try {
      await updateContact(id, contactData)
      setContact({ ...contact, ...contactData })
      setShowEditForm(false)
    } catch (error) {
      console.error('Error updating contact:', error)
      alert('Failed to update contact. Please try again.')
    }
  }

  const handleRemoveTag = async (tagToRemove) => {
    try {
      const currentTags = contact.tags || []
      const updatedTags = currentTags.filter(tag => tag !== tagToRemove)
      await updateContact(id, { tags: updatedTags })
      setContact({ ...contact, tags: updatedTags })
    } catch (error) {
      console.error('Error removing tag:', error)
      alert('Failed to remove tag. Please try again.')
    }
  }

  const handleDeleteContact = (e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    
    console.log('Delete button clicked for contact:', id)
    setShowDeleteConfirm(true)
  }

  const confirmDelete = async () => {
    console.log('User confirmed deletion, proceeding...')

    if (!user || !user.uid) {
      alert('User not authenticated. Please refresh the page.')
      setShowDeleteConfirm(false)
      return
    }

    try {
      console.log('Deleting contact:', id, 'for user:', user.uid)
      await deleteContact(id, user.uid)
      console.log('Contact deleted successfully')
      setShowDeleteConfirm(false)
      navigate('/contacts')
    } catch (error) {
      console.error('Error deleting contact:', error)
      alert(`Failed to delete contact: ${error.message || 'Unknown error'}. Check console for details.`)
      setShowDeleteConfirm(false)
    }
  }

  const cancelDelete = () => {
    console.log('Delete cancelled by user')
    setShowDeleteConfirm(false)
  }

  const handleLogActivity = async (activityData) => {
    try {
      switch (activityType) {
        case 'email':
          await logEmail(id, user.uid, activityData)
          break
        case 'call':
          await logCall(id, user.uid, activityData)
          break
        case 'meeting':
          await logMeeting(id, user.uid, activityData)
          break
        case 'website_visit':
          await logWebsiteVisit(id, user.uid, activityData)
          break
        case 'linkedin_message':
          await logLinkedInMessage(id, user.uid, activityData)
          break
        default:
          break
      }
      setShowActivityForm(false)
    } catch (error) {
      console.error('Error logging activity:', error)
      alert('Failed to log activity. Please try again.')
    }
  }

  const getActivityIcon = (type) => {
    switch (type) {
      case 'email':
        return '📧'
      case 'call':
        return '📞'
      case 'meeting':
        return '🤝'
      case 'website_visit':
        return '🌐'
      case 'linkedin_message':
        return '💼'
      default:
        return '📝'
    }
  }

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown date'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return format(date, 'PPpp')
  }

  if (loading) {
    return <div className="loading">Loading contact...</div>
  }

  if (!contact) {
    return <div className="error">Contact not found</div>
  }

  return (
    <div className="contact-detail-page">
      <div className="detail-header">
        <Link to="/contacts" className="back-link">← Back to Contacts</Link>
        <div className="header-actions">
          <button
            className="btn-secondary"
            onClick={() => setShowEditForm(true)}
          >
            Edit Contact
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={handleDeleteContact}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="detail-content">
        <div className="contact-info-section">
          <div className="contact-header-card">
            <div className="contact-avatar-large">
              {contact.name?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="contact-main-info">
              <h1>{contact.name || 'Unnamed Contact'}</h1>
              <p className="contact-company">{contact.company || 'No company'}</p>
              <div className="contact-meta">
                {contact.email && (
                  <a href={`mailto:${contact.email}`} className="meta-item">
                    📧 {contact.email}
                  </a>
                )}
                {contact.phone && (
                  <a href={`tel:${contact.phone}`} className="meta-item">
                    📞 {contact.phone}
                  </a>
                )}
              </div>
              {contact.tags && contact.tags.length > 0 && (
                <div className="contact-tags">
                  {contact.tags.map((tag, idx) => (
                    <span key={idx} className="tag">
                      {tag}
                      <button
                        className="tag-remove"
                        onClick={() => handleRemoveTag(tag)}
                        title="Remove tag"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <LinkedInWorkflow
          contact={contact}
          userId={user.uid}
          onUpdate={(updatedContact) => setContact(updatedContact)}
        />

        <div className="activity-section">
          <div className="activity-header">
            <h2>Activity Timeline</h2>
            <div className="activity-buttons">
              <button
                className="activity-btn"
                onClick={() => {
                  setActivityType('email')
                  setShowActivityForm(true)
                }}
              >
                📧 Log Email
              </button>
              <button
                className="activity-btn"
                onClick={() => {
                  setActivityType('call')
                  setShowActivityForm(true)
                }}
              >
                📞 Log Call
              </button>
              <button
                className="activity-btn"
                onClick={() => {
                  setActivityType('meeting')
                  setShowActivityForm(true)
                }}
              >
                🤝 Log Meeting
              </button>
              <button
                className="activity-btn"
                onClick={() => {
                  setActivityType('website_visit')
                  setShowActivityForm(true)
                }}
              >
                🌐 Log Visit
              </button>
              <button
                className="activity-btn"
                onClick={() => {
                  setActivityType('linkedin_message')
                  setShowActivityForm(true)
                }}
              >
                💼 Log LinkedIn Message
              </button>
            </div>
          </div>

          <div className="timeline">
            {activities.length === 0 ? (
              <div className="empty-timeline">
                <p>No activities yet. Start logging interactions with this contact!</p>
              </div>
            ) : (
              activities.map((activity) => (
                <div key={activity.id} className="timeline-item">
                  <div className="timeline-icon">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-header">
                      <span className="activity-type">
                        {activity.type?.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className="activity-date">
                        {formatTimestamp(activity.createdAt)}
                      </span>
                    </div>
                    {activity.subject && (
                      <h4 className="activity-subject">{activity.subject}</h4>
                    )}
                    {activity.description && (
                      <p className="activity-description">{activity.description}</p>
                    )}
                    {activity.duration && (
                      <p className="activity-meta">Duration: {activity.duration}</p>
                    )}
                    {activity.location && (
                      <p className="activity-meta">Location: {activity.location}</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showEditForm && (
        <ContactForm
          initialData={contact}
          onSubmit={handleUpdateContact}
          onCancel={() => setShowEditForm(false)}
        />
      )}

      {showActivityForm && (
        <ActivityForm
          type={activityType}
          onSubmit={handleLogActivity}
          onCancel={() => setShowActivityForm(false)}
        />
      )}

      {showDeleteConfirm && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-modal">
            <h3>⚠️ Delete Contact</h3>
            <p>Are you sure you want to permanently delete <strong>{contact?.name || 'this contact'}</strong>?</p>
            <p className="warning-text">This action cannot be undone. All associated activities will also be deleted.</p>
            <div className="delete-confirm-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
              >
                Delete Contact
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ContactDetail

